import { getContentfulClient } from './client';
import { resolveAssetUrl, unwrapField, unwrapLinkField } from './utils';

const CONTENT_TYPE_LAB_HOME = 'labHomePageContent';

/**
 * @param {unknown} raw
 * @returns {number | null} epoch ms, or null if not parseable
 */
function parseUpdateDateToMs(raw) {
  if (raw == null) {
    return null;
  }
  const s = String(raw).trim();
  if (!s) {
    return null;
  }
  const t = Date.parse(s);
  if (!Number.isNaN(t)) {
    return t;
  }
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  }
  return null;
}

/**
 * From all `labHomePageContent` entries, pick the one with the newest
 * `updateDate` (Symbol field, parsed as a date). Ties: newest `sys.updatedAt`.
 * @param {import('contentful').Entry<any>[]} items
 */
function pickNewestEntry(items) {
  if (!items || items.length === 0) {
    return null;
  }
  return [...items].sort((a, b) => {
    const tA = parseUpdateDateToMs(unwrapField(a.fields?.updateDate)) ?? 0;
    const tB = parseUpdateDateToMs(unwrapField(b.fields?.updateDate)) ?? 0;
    if (tB !== tA) {
      return tB - tA;
    }
    return new Date(b.sys.updatedAt).getTime() - new Date(a.sys.updatedAt).getTime();
  })[0];
}

function buildAssetsById(partialMap, includes) {
  const m = new Map(partialMap);
  (includes?.Asset || includes?.asset || []).forEach((a) => {
    if (a?.sys?.id) {
      m.set(a.sys.id, a);
    }
  });
  return m;
}

/**
 * @param {import('contentful').Entry<any>} entry
 * @param {Map<string, any>} assetsById
 */
function mapEntryToResult(entry, assetsById) {
  const f = entry.fields || {};
  const labStatementRaw = f.labStatement;
  const asDoc =
    labStatementRaw && typeof labStatementRaw === 'object' && labStatementRaw.nodeType === 'document'
      ? labStatementRaw
      : null;
  const fromUnwrap = asDoc || unwrapField(labStatementRaw);
  const labStatementDocument =
    fromUnwrap && typeof fromUnwrap === 'object' && fromUnwrap.nodeType === 'document' ? fromUnwrap : null;

  const imageUrl = (linkField) => resolveAssetUrl(unwrapLinkField(linkField), assetsById);

  const highlights = [
    {
      key: 'research-news',
      title: String(unwrapField(f.researchNewsText) || '').trim(),
      link: String(unwrapField(f.researchNewsUrl) || '').trim(),
      imageUrl: imageUrl(f.researchNewsImage),
    },
    {
      key: 'meet-the-team',
      title: String(unwrapField(f.meetTheTeamText) || '').trim(),
      link: String(unwrapField(f.meetTheTeamUrl) || '').trim(),
      imageUrl: imageUrl(f.meetTheTeamImage),
    },
    {
      key: 'research-project',
      title: String(unwrapField(f.researchProjectText) || '').trim(),
      link: String(unwrapField(f.researchProjectUrl) || '').trim(),
      imageUrl: imageUrl(f.researchProjectImage),
    },
  ];

  return {
    id: entry.sys.id,
    updateDate: String(unwrapField(f.updateDate) || '').trim(),
    labStatementDocument: labStatementDocument || null,
    assetsById,
    highlights,
  };
}

/**
 * @param {any} data
 * @returns {boolean}
 */
export function isLabHomePageDataComplete(data) {
  if (!data || !data.labStatementDocument || data.labStatementDocument.nodeType !== 'document') {
    return false;
  }
  if (!Array.isArray(data.highlights) || data.highlights.length !== 3) {
    return false;
  }
  return data.highlights.every((h) => h.title && h.link && h.imageUrl);
}

/**
 * Fetches the single newest `labHomePageContent` entry (by `updateDate`, then `sys.updatedAt`).
 * @returns {Promise<any | null>}
 */
export async function fetchNewestLabHomePageContent() {
  const client = getContentfulClient();
  if (!client) {
    return null;
  }

  const limit = 100;
  let skip = 0;
  const allItems = [];
  for (;;) {
    const listRes = await client.getEntries({
      content_type: CONTENT_TYPE_LAB_HOME,
      limit,
      skip,
      select: 'sys,fields.updateDate',
      order: 'sys.createdAt',
    });
    allItems.push(...(listRes.items || []));
    if ((listRes.items || []).length < limit) {
      break;
    }
    skip += limit;
  }

  const pick = pickNewestEntry(allItems);
  if (!pick) {
    return null;
  }

  const full = await client.getEntry(pick.sys.id, { include: 10 });
  const assetsById = buildAssetsById(new Map(), full.includes);
  return mapEntryToResult(full, assetsById);
}
